import React, { useEffect, useState } from 'react';
import { CButton, CForm, CModal, CModalBody } from '@coreui/react-pro';
import * as Yup from 'yup';
import {
     API_SUBSCRIBER_ORGANIZATION,
     API_SUBSCRIBER_ORGANIZATION_USER,
     LOCAL_CART,
     LOCAL_COUPON,
     LOCAL_COUPON_VOUCHERS,
     LOCAL_ORG_USER,
     ORG_USER_ROLES,
     POPUP_TYPE,
     STATUS,
} from '../../../../constants';
import { SimpleInputGroup, SimpleSelectGroup } from '../../../General/Forms';
import { Formik } from 'formik';
import { Waiting } from '../../../General/Loadings';
import { useDispatch, useSelector } from 'react-redux';
import { getListOptionsCountryByCountry, getListTimeZonesMoment, toastError } from '../../../../utils';
import { callApi } from '../../../../apiCaller';

import { fetchManagedOrganizationsRequest, setManagedOrganizations } from '../../Organizations/ManagedOrganizations/actions';
import PageBack from '../../../General/Link/PageBack';

import { setShow } from '../../../../actions/general';
import { fetchSubscriberLicenseesRequest } from '../../Subscriber/Licensees/actions';
import { setCurrentCountry } from '../../Subscriber/GlobalStates/CurrentCountry/actions';
import { setSubscriberUser } from '../../Subscriber/GlobalStates/CurrentUser/actions';

const validationSchema = (setValidateOnChange) => {
     setValidateOnChange(true);
     return Yup.object().shape({
          name: Yup.string().required('Required'),
          licenseeId: Yup.string().required('Required'),
          timezone: Yup.string().required('Required').nullable(),
          country: Yup.string().required('Required'),
     });
};

const CreateOrganization = () => {
     const dispatch = useDispatch();
     const user = useSelector((state) => state.subscriber.user);
     const licensees = useSelector((state) => state.subscriber.licensees);

     const currentLicensee = user?.country && licensees?.length > 0 ? licensees.find((item) => item.country.includes(user.country)) : null;

     const countryOptions = getListOptionsCountryByCountry(user?.country, licensees);

     const initialValues = {
          name: '',
          licenseeId: currentLicensee?.id,
          timezone: user?.timezone,
          status: STATUS.ENABLE,
          country: user?.country,
     };

     const [validateOnChange, setValidateOnChange] = useState(false);
     const [listTimezones, setListTimezones] = useState(getListTimeZonesMoment(user?.country));

     const fetchDataLicensee = () => {
          if (!licensees) {
               dispatch(fetchSubscriberLicenseesRequest());
          }
     };

     useEffect(() => {
          fetchDataLicensee();
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     const selectOrganization = (organizationId) => {
          callApi(API_SUBSCRIBER_ORGANIZATION_USER, 'GET').then((response) => {
               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               const managedOrganizations = response.data;
               const managedOrganizationSelected = managedOrganizations.find((item) => item.organization.id === organizationId);

               if (managedOrganizationSelected) {
                    localStorage.setItem(LOCAL_ORG_USER, JSON.stringify(managedOrganizationSelected));
               }

               dispatch(setManagedOrganizations(managedOrganizations));
          });
     };

     const handleSubmitForm = (values, { setSubmitting }) => {
          setSubmitting(true);

          callApi(API_SUBSCRIBER_ORGANIZATION, 'POST', values)
               .then((response) => {
                    if (response.status !== 200) {
                         toastError(response);
                         return;
                    }

                    selectOrganization(response.data.id);

                    const newOrg = {
                         ...response.data,
                         OrganizationUsers: {
                              role: ORG_USER_ROLES.OWNER,
                         },
                    };

                    user.organizations.push(newOrg);

                    dispatch(setSubscriberUser(user));
                    dispatch(setCurrentCountry(newOrg.country));
                    dispatch(fetchManagedOrganizationsRequest());

                    localStorage.removeItem(LOCAL_CART);
                    localStorage.removeItem(LOCAL_COUPON);
                    localStorage.removeItem(LOCAL_COUPON_VOUCHERS);
               })
               .finally(() => {
                    setSubmitting(false);
                    dispatch(setShow(null));
               });
     };

     const handleBackClick = () => {
          dispatch(setShow(POPUP_TYPE.SELECT_OR_CREATE_ORG));
     };

     const handleCloseButtonClick = () => {
          dispatch(setShow(null));
     };

     return (
          <CModal alignment="center" visible={true} onClose={handleCloseButtonClick} className="popup-custom create-org-popup">
               <CModalBody>
                    <PageBack className="back-link-popup" onClick={handleBackClick} title="BACK" />

                    <h2 className="text-center popup-title">Create Your Organization</h2>
                    <div className="popup-description">
                         <p>
                              You can build a shopping cart and place an order for only one organization. Organizations can purchase multiple seats
                              for training products and offer those seats to individuals to complete courses. Think of an Organization as a business,
                              company, health care group, or non profit.
                         </p>
                         <p>
                              Please create an organization below for this purchase. If you already have a CAMS-care account, please log in before
                              proceeding
                         </p>
                    </div>

                    <Formik
                         initialValues={initialValues}
                         validationSchema={() => validationSchema(setValidateOnChange)}
                         onSubmit={handleSubmitForm}
                         validateOnChange={validateOnChange}
                         validateOnBlur={false}
                    >
                         {({ values, errors, handleChange, setFieldError, setFieldValue, handleSubmit, isSubmitting }) => {
                              // const timezoneValue = listTimezones.find((tz) => tz.value === values.timezone);

                              const handleChangeCountry = (e) => {
                                   const countryValue = e.target.value;

                                   if (countryValue) {
                                        setListTimezones(getListTimeZonesMoment(countryValue));
                                   } else {
                                        setListTimezones([]);
                                   }
                                   setFieldValue('country', countryValue);
                                   setFieldValue('timezone', null);

                                   if (countryValue !== '') {
                                        setTimeout(() => {
                                             setFieldError('country', undefined);
                                        }, 0);
                                   }
                              };

                              const handleChangeTimeZone = (e) => {
                                   const timezoneValue = e.target.value;
                                   setFieldValue('timezone', timezoneValue);
                              };

                              return (
                                   <CForm noValidate onSubmit={handleSubmit}>
                                        <SimpleInputGroup
                                             id="name"
                                             label="Organization Name"
                                             className="input-custom-wrap"
                                             classNameInput="input-custom"
                                             isRequired
                                             value={values.name}
                                             error={errors.name}
                                             disabled={isSubmitting}
                                             handleChange={handleChange}
                                        />
                                        <SimpleSelectGroup
                                             id="country"
                                             name="country"
                                             label="Organization Country "
                                             className="input-custom-wrap"
                                             classNameInput="input-custom dropdown-custom"
                                             isRequired
                                             optionNameDefault="Select Country..."
                                             value={values.country}
                                             error={errors.country}
                                             disabled={isSubmitting}
                                             data={countryOptions}
                                             handleChange={handleChangeCountry}
                                        />
                                        <SimpleSelectGroup
                                             id="timezone"
                                             label="Organization Time Zone "
                                             className="input-custom-wrap"
                                             classNameInput="input-custom dropdown-custom"
                                             isRequired
                                             optionNameDefault="Select Time Zone..."
                                             value={values.timezone}
                                             error={errors.timezone}
                                             disabled={isSubmitting || values.country === ''}
                                             data={listTimezones}
                                             handleChange={handleChangeTimeZone}
                                        />
                                        {/* <SelectSearchField
                                             id="timezone"
                                             label="Organization Time Zone "
                                             classNameWrap="input-custom-wrap"
                                             classNameInput="input-custom select-search-custom"
                                             placeholder="Select Time Zone..."
                                             isRequired
                                             options={listTimezones}
                                             selectInputType={false}
                                             value={timezoneValue}
                                             error={errors.timezone}
                                             disabled={isSubmitting || values.country === ''}
                                             onChange={(e) => setFieldValue('timezone', e.value)}
                                        /> */}

                                        <div className="login-section-card-button mt-4">
                                             <CButton className="btn-blue-highlight btn-h-50 btn-border" type="submit" disabled={isSubmitting}>
                                                  <Waiting isLoading={isSubmitting}>Create new Organization</Waiting>
                                             </CButton>
                                        </div>
                                   </CForm>
                              );
                         }}
                    </Formik>
               </CModalBody>
          </CModal>
     );
};

export default CreateOrganization;
